import React from 'react'
import { createRoot } from 'react-dom/client'
import Modal from '../components/Modal'

export interface ConfirmModalProps {
  titleMsg: string
  msg: React.ReactNode
  cancelTxt?: string
  okTxt?: string
  onSubmit?: () => void | Promise<void>
  onCancel?: () => void
  loading?: boolean
}

interface ModalConfirmationProps extends ConfirmModalProps {
  onClose: () => void
}

function ModalConfirmation({
  titleMsg,
  msg,
  cancelTxt = 'Cancel',
  okTxt = 'OK',
  onSubmit,
  onCancel,
  onClose,
  loading = false
}: ModalConfirmationProps) {
  const [show, setShow] = React.useState(true)
  const [isLoading, setIsLoading] = React.useState(loading)

  const handleClose = () => {
    setShow(false)
    setTimeout(onClose, 300) // Wait for animation
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    handleClose()
  }

  const handleSubmit = async () => {
    if (onSubmit) {
      setIsLoading(true)
      try {
        await onSubmit()
      } finally {
        setIsLoading(false)
      }
    }
    handleClose()
  }

  return (
    <Modal
      show={show}
      handleClose={handleCancel}
      title={titleMsg}
      size="sm"
      isConfirmationDialog={true}
      className="theme-sandman"
    >
      <Modal.Body>
        <div style={{ wordBreak: 'break-word' }}>{msg}</div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="secondary" onClick={handleCancel}>
          {cancelTxt}
        </Modal.Footer.Button>
        <Modal.Footer.Button variant="primary" onClick={handleSubmit}>
          {okTxt}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

/**
 * Display a confirmation modal dialog
 * Returns a cleanup function to remove the modal from DOM
 */
export function confirmModal(props: ConfirmModalProps): () => void {
  const containerElement = document.createElement('div')
  containerElement.className = 'theme-sandman'
  document.body.appendChild(containerElement)

  const root = createRoot(containerElement)

  const cleanup = () => {
    root.unmount()
    document.body.removeChild(containerElement)
  }

  root.render(<ModalConfirmation {...props} onClose={cleanup} />)

  return cleanup
}
